import { Inject, Injectable } from '@angular/core';
import { timer } from 'rxjs';
import { takeWhile, map } from 'rxjs/operators';
import _, { initial }  from 'lodash';
import { Console } from 'console';

interface MySynchMetric{
    mysynchMetricCode:string;
    decimalPlaces:number;
    isPercent:boolean;
}

interface IntlNumberFormatOptions {
    style: string;
    minimumFractionDigits: number;
    maximumFractionDigits: number;
}

@Injectable({
    providedIn: 'root',
})
export class MySynchFormatterService {
    private maxWaitTime = 5000; // Maximum wait time in milliseconds
    private pollInterval = 100; // Polling interval in milliseconds
    
    private mySynchMetrics:MySynchMetric[] = [];


    constructor( @Inject('MySynchFactory') private MSF:any){
        this.waitForCodeFileList().then(() => this.initializeMySynchMetrics());
    }

    private waitForCodeFileList(): Promise<void> {
        return new Promise((resolve, reject) => {
          const startTime = Date.now();
          
          const checkProperty = timer(0, this.pollInterval).pipe(
            map(() => { 
                let codeFile = _.get(this.MSF,'lists.codeFile');
                return Array.isArray(codeFile) && codeFile.length > 0
              }
            ),
            takeWhile((isAvailable) => !isAvailable && Date.now() - startTime < this.maxWaitTime, true)
          );
    
          checkProperty.subscribe({
            next: (isAvailable) => {
              if (isAvailable) {
                resolve();
              }
            },
            complete: () => {
             console.error('Timed out waiting for MSF.someProperty');
             reject();
            }
          });
        });
    }

    private initializeMySynchMetrics(){
         let cfMySynchMetrics =  _.filter(_.get(this.MSF,'lists.codeFile',[]),{codeType:'MYSYNCHMETRICS'}); 
         if(!_.isEmpty(cfMySynchMetrics)){
            _.forEach(cfMySynchMetrics,(metric)=>{
                let detailsJson = {};
                let cfDetailsJson = _.get(metric,'detailsJson',"");
                try {
                    detailsJson = JSON.parse(cfDetailsJson)[0];
                } catch (error) {
                    console.log("Error parsing detailsJson for metric: ", _.get(metric,'code'));
                }

                let mysynchMetric = {
                    mysynchMetricCode: _.get(metric,'code'),
                    decimalPlaces: _.get(detailsJson,'decimalPlaces',2),
                    isPercent: _.get(detailsJson,'isPercent',false)
                } as MySynchMetric;
                this.mySynchMetrics.push(mysynchMetric);
            }); 
         }
         
    }
    /**
     * Checks if a value is a finite number.
     * 
     * @param {any} value - The value to check.
     * @returns {boolean} - True if the value is a finite number, false otherwise.
     */
    private isNumber(value:any): boolean {
        return _.isFinite(value);
    }


    /**
     * Checks if a number is negative.
     * 
     * @param {number} number - The number to check.
     * @returns {boolean} - True if the number is negative, false otherwise.
     */
    private isNegativeNumber(number:number):boolean {
        return number < 0;
    }
        /**
     * Rounds a number to a specified number of decimal places.
     * 
     * @param {number} rawNumber - The number to be rounded.
     * @param {number} decimalPlaces - The number of decimal places to round to.
     * @returns {number} - The rounded number to the specified number of decimal places.
     */    
    private getPreciseNumber(rawNumber:number, decimalPlaces:number):number {
    const factor = Math.pow(10, decimalPlaces);
    return Math.round((rawNumber + Number.EPSILON) * factor) / factor;
    }
    /**
     * Converts a decimal value to a fractional number.
     * 
     * @param {number} decimalValue - The decimal value to be converted.
     * @returns {number} - The fractional number representation of the decimal value.
     */
    private getFractionalNumber(decimalValue:number):number {
        let isNegative = this.isNegativeNumber(decimalValue);
        let wholePart = Math.trunc(decimalValue);
        let numberOfDigits = wholePart.toString().replace(/^-/g, '').length;
        let divisor = Math.pow(10, numberOfDigits);
        let absFractionalValue = Math.abs(decimalValue / divisor);
        let fractionalValue = isNegative ? -absFractionalValue : absFractionalValue;
        return fractionalValue;
    }
    /**
     * Converts a fractional value to a whole number percentage.
     * 
     * @param {number} fractionalPctValue - The fractional percentage value to be converted.
     * @returns {number} - The whole number percentage representation of the fractional percentage value -> Math.round(fractionalPctValue * 100)
     **/
    private getPercentWholeNumber(fractionalPctValue:number):number {
        return Math.round(fractionalPctValue * 100);
    }

    
    /**
     * Formats a number value into Intl represented format based on the specified options.
     * 
     * @param {number} value - The number value to be formatted.
     * @param {IntlNumberFormatOptions} formatOptions - The options to be used for formatting the number value.
     * @param {string} locale (default:undefined) - The locale to be used for formatting the number value. Supplying undefined defaults to the systems locale.
     * @returns {string} The formatted number value with Intl representation.
     **/   
    getIntlStandardizedNumberorUnit(value:number, formatOptions:Intl.NumberFormatOptions,locale:string = undefined):string{
       // formateOptions and locale are optional agruments and should have a value of undefined when 
		// they are not intentially set.
        if(!_.isUndefined(formatOptions) && (_.isNull(formatOptions) || _.isEmpty(formatOptions))){
            formatOptions = undefined;
        }

        if(!_.isUndefined(locale) && (_.isNull(formatOptions) || _.isEmpty(_.trim(locale)))){
            locale = undefined;
        }

         return new Intl.NumberFormat(locale,formatOptions).format(value);
    }

    /**
     *  Gets the metric value of precision based its configuration.
     * @param {number} value  - the metric value to be changed to the correct precision
     * @param {string} metricCode - the code to identify the metric
     * @returns {number} - the metric value of precision
     */
    getMetricValueOfPrecision(value:number, metricCode:string):number {
        // Since this service will be used in an AngularJS context, we need to ensure that the value is a number
        if(!this.isNumber(value)) {
            console.error('Invalid value provided to getRiskValueOfPrecision');
            return value;
        }

        let metric = _.find(this.mySynchMetrics, {mysynchMetricCode: metricCode});
        if(_.isNull(metric) || _.isUndefined(metric)) {
            console.error('Invalid metric code provided to getMetricValueOfPrecision');
            return value;
        }

        let decimalPlaces = metric.decimalPlaces;

        return this.getPreciseNumber(value, decimalPlaces);
    }

    /**
     *  Formats a metric number value based on the specified metric code and options.
     * @param {number} value - the metric value to be formatted
     * @param {string} metricCode - the code to identify the metric
     * @param {boolean} fractionalOutput  - indicates if the value should be formatted as a fractional output
     * @param {boolean} includePercent - indicates if the value should include a percent sign
     * @returns {string} - the formatted metric number value
     */
    formatMetricNumberValue(value:number, metricCode:string, fractionalOutput:boolean = false, includePercent:boolean = false):string {
        // Since this service will be used in an AngularJS context, we need to ensure that the value is a number
        if(!this.isNumber(value)) {
            console.error('Invalid value provided to formatRiskNumberValue returning original value as string');
            return value.toString();
        }
        
        let metric = _.find(this.mySynchMetrics, {mysynchMetricCode: metricCode});
        if(_.isNull(metric) || _.isUndefined(metric)) {
            console.error(`Invalid metric code(${metricCode}) provided to formatMetricNumberValue`);
            return value.toString();
        }

        let wholePart = Math.trunc(value);
        let fractionalPart = Math.abs(value - wholePart); 
        // is the value less than 1 and greater than 0
        let valueIsFractional = fractionalPart > 0 && wholePart === 0;
        let isPercentValue = metric.isPercent;

        
        let preciseValue = 0;
        let formattedValue = '';
        const intlOptions:Intl.NumberFormatOptions = {
            style: 'decimal',
            minimumFractionDigits: metric.decimalPlaces,
            maximumFractionDigits: metric.decimalPlaces
        };
        // if the value is a percent value, we may need to convert it to a whole number or fractional number depending on the intended output
        if(isPercentValue) {
            let correctedPctValue = value;
            if(fractionalOutput && !valueIsFractional) {
                correctedPctValue = this.getFractionalNumber(correctedPctValue);    
            }
            
            if(!fractionalOutput && valueIsFractional) {
                correctedPctValue = this.getPercentWholeNumber(correctedPctValue);
            }
            preciseValue =  this.getMetricValueOfPrecision(correctedPctValue, metricCode);
            formattedValue =  this.getIntlStandardizedNumberorUnit(preciseValue,intlOptions); 
            if(includePercent) {
                formattedValue += '%';
            }
        }else{
            preciseValue = this.getMetricValueOfPrecision(value, metricCode);
            formattedValue =  this.getIntlStandardizedNumberorUnit(preciseValue,intlOptions); 
        }

        return formattedValue;

    }

    // ----------------------------------------------

}